/**
 * Route table — per-route code splitting via react-router `lazy` (§11.2):
 * every destination is its own dynamic import, so the initial bundle carries
 * only the shell + login path. Guards wrap layout routes, so a lazy chunk is
 * never even requested for an unauthorized visit (§11.1).
 */

import { Outlet, type RouteObject } from "react-router-dom";
import { RedirectIfAuthenticated, RequireAuth } from "../auth/guards";
import { AdminShell } from "../layout/AppShell";

export function buildRoutes(): RouteObject[] {
  return [
    {
      element: (
        <RedirectIfAuthenticated>
          <Outlet />
        </RedirectIfAuthenticated>
      ),
      children: [
        {
          path: "/login",
          lazy: () => import("../screens/login/LoginScreen"),
        },
      ],
    },
    {
      path: "/",
      element: (
        <RequireAuth>
          <AdminShell />
        </RequireAuth>
      ),
      children: [
        { index: true, lazy: () => import("../screens/dashboard/DashboardScreen") },
        {
          path: "orders",
          children: [
            { index: true, lazy: () => import("../screens/orders/OrdersListScreen") },
            { path: ":orderId", lazy: () => import("../screens/orders/OrderDetailScreen") },
          ],
        },
        { path: "dispatch", lazy: () => import("../screens/dispatch/DispatchMapScreen") },
        // M7-07 — the §13 rule catalog and the assignment audit browse are
        // their own destinations: each is gated on its own dispatch.* key,
        // and neither belongs inside the live map's realtime surface.
        { path: "dispatch-rules", lazy: () => import("../screens/dispatch/DispatchRulesScreen") },
        { path: "assignments", lazy: () => import("../screens/dispatch/AssignmentsScreen") },
        // M7-74 — orders dispatch could not place. Its own destination: the
        // reason lives in the audit trail, not on the map's realtime feed.
        { path: "unassigned-orders", lazy: () => import("../screens/dispatch/UnassignedOrdersScreen") },
        // §6.6 override approval queue (M6-19) — its own destination, gated on
        // `dispatch.override.decide` rather than the map's permissions.
        { path: "geofence-overrides", lazy: () => import("../screens/dispatch/GeofenceOverridesScreen") },
        {
          path: "zones",
          children: [
            { index: true, lazy: () => import("../screens/zones/ZonesListScreen") },
            { path: "new", lazy: () => import("../screens/zones/ZoneEditorScreen") },
            // Static segment BEFORE the dynamic one — "geofence" is not a zone id.
            { path: "geofence", lazy: () => import("../screens/zones/GeofenceRadiiScreen") },
            { path: ":zoneId", lazy: () => import("../screens/zones/ZoneEditorScreen") },
          ],
        },
        {
          path: "merchants",
          children: [
            { index: true, lazy: () => import("../screens/merchants/MerchantsListScreen") },
            { path: "new", lazy: () => import("../screens/merchants/MerchantCreateScreen") },
            { path: ":merchantId", lazy: () => import("../screens/merchants/MerchantDetailScreen") },
          ],
        },
        // M7-148 — store categories: its own destination, so the segment is
        // never read as a merchant id.
        { path: "store-categories", lazy: () => import("../screens/merchants/StoreCategoriesScreen") },
        // M7-43b — §14.5 join-flow review queue (its own destination beside
        // Merchants, so "merchant-applications" is never read as a merchant id).
        { path: "merchant-applications", lazy: () => import("../screens/merchants/MerchantApplicationsScreen") },
        {
          path: "drivers",
          children: [
            { index: true, lazy: () => import("../screens/drivers/DriversListScreen") },
            // Static segment BEFORE the dynamic one — "new" is not a driver id.
            { path: "new", lazy: () => import("../screens/drivers/DriverCreateScreen") },
            { path: ":driverId", lazy: () => import("../screens/drivers/DriverDetailScreen") },
          ],
        },
        // M7-150 — vehicle types: its own destination, so the segment is
        // never read as a driver id.
        { path: "vehicle-types", lazy: () => import("../screens/drivers/VehicleTypesScreen") },
        {
          path: "customers",
          children: [
            { index: true, lazy: () => import("../screens/customers/CustomersListScreen") },
            { path: ":customerId", lazy: () => import("../screens/customers/CustomerDetailScreen") },
          ],
        },
        {
          path: "wallets",
          children: [
            { index: true, lazy: () => import("../screens/wallets/WalletsListScreen") },
            { path: ":ownerType/:ownerId", lazy: () => import("../screens/wallets/WalletDetailScreen") },
          ],
        },
        {
          path: "settlement",
          children: [
            { index: true, lazy: () => import("../screens/settlement/SettlementScreen") },
            { path: "records/:settlementId", lazy: () => import("../screens/settlement/SettlementRecordDetailScreen") },
          ],
        },
        {
          path: "subscriptions",
          children: [
            { index: true, lazy: () => import("../screens/subscriptions/SubscriptionsScreen") },
            { path: ":subscriptionId", lazy: () => import("../screens/subscriptions/SubscriptionDetailScreen") },
          ],
        },
        // M7-38 — §7.2 cross-merchant engagement reports (Growth).
        { path: "engagement", lazy: () => import("../screens/merchants/EngagementReportsScreen") },
        {
          path: "promotions",
          children: [
            { index: true, lazy: () => import("../screens/promotions/PromotionsScreen") },
            { path: "new", lazy: () => import("../screens/promotions/PromotionCreateScreen") },
            { path: ":promotionId", lazy: () => import("../screens/promotions/PromotionDetailScreen") },
          ],
        },
        // M7-106 — Wave 14 customer-app ad banners (marketing.yaml). The
        // screens gate themselves on the `customer_ads` flag + permissions.
        {
          path: "ads",
          children: [
            { index: true, lazy: () => import("../screens/ads/AdsListScreen") },
            // Static segment BEFORE the dynamic one — "new" is not a banner id.
            { path: "new", lazy: () => import("../screens/ads/AdBannerEditorScreen") },
            { path: ":bannerId", lazy: () => import("../screens/ads/AdBannerEditorScreen") },
          ],
        },
        // M7-182 — Wave 22 customer home sections (marketing.yaml v1.2.0).
        { path: "home-sections", lazy: () => import("../screens/homeSections/HomeSectionsScreen") },
        // M7-253 — admin-managed store collections on the customer home (marketing.yaml v1.4.0).
        { path: "collections", lazy: () => import("../screens/collections/CollectionsScreen") },
        // M7-266 — per-category layout: subcategories + slots (marketing.yaml v1.5.0).
        { path: "category-layout", lazy: () => import("../screens/categoryLayout/CategoryLayoutScreen") },
        // M7-269 — the customer home's admin-arranged middle block (marketing.yaml v1.6.0).
        { path: "home-layout", lazy: () => import("../screens/homeLayout/HomeLayoutScreen") },
        // M7-301 — the customer app's image pop-ups (marketing.yaml v1.7.0).
        { path: "popups", lazy: () => import("../screens/popups/PopupsScreen") },
        { path: "notifications", lazy: () => import("../screens/notifications/NotificationTemplatesScreen") },
        {
          path: "settings",
          children: [
            { index: true, lazy: () => import("../screens/settings/SystemSettingsScreen") },
            { path: "theme", lazy: () => import("../screens/theme/ThemeManagementScreen") },
            { path: "providers", lazy: () => import("../screens/payments/PaymentProvidersScreen") },
          ],
        },
        // M7-309 — the privacy policy (admin legal surface).
        { path: "privacy-policy", lazy: () => import("../screens/legal/PrivacyPolicyScreen") },
        { path: "feature-flags", lazy: () => import("../screens/flags/FeatureFlagsScreen") },
        // M7-23 — the RBAC management area (identity.yaml roles/permissions).
        {
          path: "roles",
          children: [
            { index: true, lazy: () => import("../screens/roles/RolesScreen") },
            // Static segment BEFORE the dynamic one — "users" is not a role id.
            { path: "users", lazy: () => import("../screens/roles/UserRolesScreen") },
            { path: ":roleId", lazy: () => import("../screens/roles/RoleDetailScreen") },
          ],
        },
        { path: "audit", lazy: () => import("../screens/audit/AuditBrowseScreen") },
        { path: "*", lazy: () => import("../screens/NotFoundScreen") },
      ],
    },
  ];
}
